"use client";

import { useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { TopBar } from "@/components/layout/top-bar";
import { StarField } from "@/components/layout/star-field";
import { Toaster } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import { AuthProvider, useAuth } from "@/contexts/auth-context";

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, logout } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const publicRoutes = ["/login", "/E"];
  const isPublicRoute = publicRoutes.some((route) => pathname.startsWith(route));

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9]">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-2 border-stellar-gold/30 border-t-stellar-gold animate-spin mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">加载中...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !isPublicRoute) {
    router.replace("/login");
    return null;
  }

  if (pathname === "/login") {
    return <>{children}</>;
  }

  const isLingtiRoute = pathname.startsWith("/E");

  return (
    <>
      <StarField />
      <div className="relative z-10 flex flex-col h-screen">
        {isLingtiRoute ? (
          <main className="flex-1 overflow-y-auto overflow-x-hidden">
            {children}
          </main>
        ) : (
          <>
            <TopBar
              onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
              sidebarCollapsed={sidebarCollapsed}
              onLogout={logout}
            />
            <div className="flex flex-1 overflow-hidden">
              <AppSidebar
                collapsed={sidebarCollapsed}
                onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
              />
              <main className={cn(
                "flex-1 overflow-y-auto overflow-x-hidden transition-all duration-300",
                sidebarCollapsed ? "ml-0" : ""
              )}>
                <div className="w-[98%] mx-auto py-4 px-2">
                  {children}
                </div>
              </main>
            </div>
          </>
        )}
      </div>
      <Toaster position="top-right" closeButton />
    </>
  );
}

export function AppProviders({ children }: { children: React.ReactNode }) {
  return (
    <AuthProvider>
      <AuthGuard>
        {children}
      </AuthGuard>
    </AuthProvider>
  );
}
